import { HttpClient, httpResource } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { Observable, catchError, throwError } from "rxjs";
import { environment } from "../../environments/environment";
import { AppEvent } from "../models/app-event";
import { Project } from "../models/project";
import { StakeholderGroup } from "../models/stakeholder-group";
import { TopicOfInterest } from "../models/topic-of-interest";
import { extractErrorMessage } from "../utils/utils";


export interface UploadBatch {
  id: number;
  name: string;
  validCount: number;
  invalidCount: number;
  unprocessedCount: number;
  uploadedBy: string | null;
  uploadedOn: string | null;
  events: AppEvent[];
  projects: Project[];
}

export interface ContactUploadItem {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  organisation: string;
  stakeholderGroups: string;
  topicsOfInterest: string;
}

export interface ContactImportItem {
  uploadItem: ContactUploadItem;
  validStakeholderGroups: StakeholderGroup[];
  invalidStakeholderGroups: string[];
  validTopicsOfInterest: TopicOfInterest[];
  invalidTopicsOfInterest: string[];
  contactSimilarities: {
    matchingItems: [],
    similarItems: []
  },
  organisationSimilarities: {
    matchingItems: [],
    similarItems: []
  }
}

export interface ContactImportRequest {
  contactId: number | null; // if null, create new, otherwise update 
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  organisationId: number | null; // if null, create new, otherwise update ,
  organisationName: string;
  isOrganisationName: boolean; // true if name should be used for name, false if name should be used for acronym
  stakeholderGroups: StakeholderGroup[];
  topicsOfInterest: TopicOfInterest[];
}

@Injectable({
  providedIn: "root",
})
export class ImportService {
  baseUrl: string = environment.api_url;
  readonly refreshTrigger = signal(0);

  batches = httpResource<UploadBatch[]>(() => {
    // Cette fonction est réactive :
    // Si refreshTrigger change, la requête est re-exécutée.
    this.refreshTrigger();
    return `${this.baseUrl}/upload/batches`;
  });

  constructor(private readonly http: HttpClient) { }

  getBatchItemsById(batchId: number): Observable<ContactImportItem[]> {
    console.debug("getBatchItemsById", batchId);
    return this.http
      .get<ContactImportItem[]>(`${this.baseUrl}/import/batches/${batchId}/items`)
      .pipe(catchError((err) => throwError(() => extractErrorMessage(err))));
  }

  importUploadItem(batchId: number, itemId: number, data: ContactImportRequest): Observable<any> {
    console.debug("importUploadItem", batchId, itemId);
    return this.http
      .post<void>(`${this.baseUrl}/import/batches/${batchId}/items/${itemId}`, data)
      .pipe(catchError((err) => throwError(() => extractErrorMessage(err))));
  }

  deleteUploadItem(batchId: number, itemId: number): Observable<any> {
    console.debug("deleteUploadItem", itemId);
    return this.http
      .delete<void>(`${this.baseUrl}/import/batches/${batchId}/items/${itemId}`)
      .pipe(catchError((err) => throwError(() => extractErrorMessage(err))));
  }

  refreshBatches() {
    this.refreshTrigger.update(v => v + 1);
  }

  deleteBatch(batchId: number): Observable<any> {
    console.debug('deleteBatch', batchId);
    return this.http.delete(`${this.baseUrl}/import/batches/${batchId}`).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  } 
}
